/* eslint-disable @typescript-eslint/restrict-template-expressions */

import { boot } from '@dazn/bootstrap-lib';

import {
    BRANDS,
    environments,
    NATIVE_APP_IDENTIFIER,
    onetrustDelayInMs,
    validNativeApps,
} from '../configuration/constants';

import { appendScriptBySource, appendScriptByURL } from './appendScript';
import { getBannerLanguage } from './getBannerLanguage';
import { getGDPRConsent } from './getGdprConsent';
import { liverampInstance } from './getLiveRamp';
import { getOneTrustConsentGroups } from './getOneTrustConsentGroups';
import { hideOldBannerElements } from './hideOldBannerElements';
import { trackLiveRampAction } from './liverampNewRelicTracking';
import { getDeleteAndGetCookieSource } from './oneTrust';
import { Optanon } from './Optanon';

export interface RunCookieBannerProps {
    lifecycleEvents: {
        onChapterLoad: (clbk: () => void) => void;
    };
    chapterLanguage: boot.config.Language;
    chapterName: boot.config.ChapterName;
    startupCountry: boot.config.AnyCountry | undefined;
    environment: boot.config.Environment;
}

interface AppendScriptProps
    extends Omit<RunCookieBannerProps, 'lifecycleEvents'> {}

const setLanguage = (langUrl: string) => {
    const langPattern = /\/([a-z]{2}-[a-z]{2})\//i;
    const match = langUrl.match(langPattern);
    if (!match) return;

    const htmlElement = document.documentElement;
    htmlElement.setAttribute('lang', match[1]);
};

export function createCookieBanner(window: Window & { Optanon?: Optanon }) {
    const isNative = () => {
        const urlParams = new URLSearchParams(window.location?.search);
        const nativeApp = (
            urlParams.get(NATIVE_APP_IDENTIFIER) ||
            localStorage.getItem(NATIVE_APP_IDENTIFIER) ||
            ''
        ).toLowerCase();

        return (
            validNativeApps.includes(nativeApp) ||
            urlParams.get('hideCookieConsent') === 'true'
        );
    };

    const findAndDeleteCookieScripts = (
        {
            environment,
            chapterLanguage,
            chapterName,
            startupCountry,
        }: AppendScriptProps,
        isNewOnetrustCodeEnabled: boolean | null,
        appID: string | null,
        scriptURL: string | null,
    ) => {
        const brandName = window?.dazn?.brand?.brandName;
        if (brandName !== BRANDS.DAZN) {
            window.dispatchEvent(new CustomEvent('consent.liveramp'));
        }

        if (isNative() || brandName !== BRANDS.DAZN) return;

        const scriptTags = document.head.querySelectorAll('script.protected');
        scriptTags.forEach(scriptTag => {
            const src = scriptTag.getAttribute('src');
            const scriptContent = scriptTag.textContent?.trim();
            if (
                src?.includes(appID ?? '') ||
                scriptContent === 'function OptanonWrapper() { }'
            ) {
                scriptTag.parentNode?.removeChild(scriptTag);
            }
        });

        if (isNewOnetrustCodeEnabled) {
            appendOneTrustScriptBySource(appID, scriptURL);
        } else {
            appendOnetrustAndScripts(
                {
                    environment,
                    chapterLanguage,
                    chapterName,
                    startupCountry,
                },
                appID,
                scriptURL,
            );
        }
        removeOnetrustOverlay();
    };

    const removeOnetrustOverlay = () => {
        const sdk = document.getElementById('onetrust-pc-sdk');
        const overlay = document.querySelector('.onetrust-pc-dark-filter');
        if (sdk && sdk.classList.contains('ot-hide')) {
            if (overlay && !overlay.classList.contains('ot-hide')) {
                overlay.classList.add('ot-hide');
            }
        }
    };

    const appendOnetrustAndScripts = (
        {
            environment,
            chapterLanguage,
            chapterName,
            startupCountry,
        }: AppendScriptProps,
        appID: string | null,
        scriptURL: string | null,
    ) => {
        try {
            if (isNative()) return;
            // Append Onetrust script only if token and viewerId are present
            appendScriptBySource(getDeleteAndGetCookieSource());
        } catch (error: unknown) {
            console.error('Error fetching partner token:', error);
        } finally {
            if (isNative()) return;
            // Always append other scripts
            appendScriptByURL(scriptURL ?? '', {
                'data-domain-script': appID ?? '',
                'data-language': getBannerLanguage({
                    chapterName,
                    startupCountry,
                    chapterLanguage,
                }),
                'data-document-language': 'true',
            });

            appendScriptBySource('function OptanonWrapper() { }');
        }
    };

    const appendOneTrustScriptBySource = (
        appID: string | null,
        scriptURL: string | null,
    ) => {
        if (isNative()) return;

        appendScriptBySource('function OptanonWrapper() { }');

        // eslint-disable-next-line @applitools/compat/no-async-await
        (async function () {
            function addCode(code: string) {
                const JS = document.createElement('script');
                JS.text = code;
                document.head.appendChild(JS);
            }
            function delay(ms: number) {
                return new Promise(resolve => setTimeout(resolve, ms));
            }
            await delay(onetrustDelayInMs); // 2 second delay
            const viewerId =
                window.dazn.auth.unstable_getDecodedJWT()?.viewerId;

            if (window.PARTNER_TOKEN && viewerId) {
                // eslint-disable-next-line no-var
                var s = `var OneTrust = { dataSubjectParams: { id: "${viewerId}", isAnonymous: false, token: "${window.PARTNER_TOKEN}" } }`;
                addCode(s);
            }

            const domainId = appID ?? '';
            const head = document.getElementsByTagName('head')[0];
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptURL ?? '';
            script.setAttribute('data-domain-script', domainId);
            script.setAttribute('data-document-language', 'true');
            script.async = false;
            head.appendChild(script);
        })();
    };

    const appendGTMConsentScript = (brandName: string) => {
        if (typeof window === 'undefined') return;
        const head = document.getElementsByTagName('head')[0];
        const scriptTag = document.createElement('script');
        scriptTag.type = 'text/javascript';
        scriptTag.text = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            ad_storage: "granted",
            analytics_storage: "granted",
            ad_user_data: "granted",
            ad_personalization: "granted",
            region: ${
                brandName === BRANDS.KAYO ||
                brandName === BRANDS.BINGE ||
                brandName === BRANDS.FOXTELGNC ||
                brandName === BRANDS.FOXSPORTS
                    ? "['AU']"
                    : "['CA', 'JP', 'US']"
            }
          });
          gtag('consent', 'default', {
            ad_storage: "denied",
            analytics_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied"
          });
        `;
        head.appendChild(scriptTag);
    };

    const run = (
        {
            environment,
            lifecycleEvents,
            startupCountry,
            chapterLanguage,
            chapterName,
        }: RunCookieBannerProps,
        isEnabledAnonymous: boolean | null,
        isLiverampEnabled: boolean | null,
        rampIdExpiryInMillis: number | null,
        isPairIdEnabled: boolean | null,
        isATSDEnabled: boolean | null,
        appID: string | null,
        scriptURL: string | null,
    ) => {
        const brandName = window?.dazn?.brand?.brandName;
        appendGTMConsentScript(brandName);
        const handleLiverampSetup = () => {
            new Promise(resolve => setTimeout(resolve, onetrustDelayInMs)).then(
                () => {
                    const token = window.dazn.auth.token;
                    const viewerId =
                        window.dazn.auth.unstable_getDecodedJWT()?.viewerId;

                    if (token && viewerId) {
                        if (isLiverampEnabled) {
                            liverampInstance.initLiveRampId(
                                rampIdExpiryInMillis,
                                isPairIdEnabled,
                                isATSDEnabled,
                            );
                        } else {
                            trackLiveRampAction('LiveRamp_Skipped', {
                                reason: 'disabled',
                            });
                            liverampInstance.deleteLiveRampCookies();
                        }
                    }
                },
            );
        };

        if (
            environments.TESTING === environment ||
            brandName === BRANDS.KAYO ||
            brandName === BRANDS.BINGE ||
            brandName === BRANDS.FOXTELGNC ||
            brandName === BRANDS.FOXSPORTS
        ) {
            hideOldBannerElements();
            lifecycleEvents.onChapterLoad(() => {
                handleLiverampSetup();
                window.addEventListener(
                    'consent.liveramp',
                    handleLiverampSetup,
                );
            });
            return;
        }

        lifecycleEvents.onChapterLoad(() => {
            const showOldBannerElements = hideOldBannerElements();
            window.addEventListener('consent.onetrust', () => {
                // OneTrust triggers the event before `window.Optanon` is constructed,
                // so we have to rely on an interval until they are really available
                let optanonExistsIntervalCount = 0;
                const optanonExistsMaxRetries = 10;
                const OPTANON_CHECK_INTERVAL = 1000;
                const optanonExistsInterval = window.setInterval(() => {
                    optanonExistsIntervalCount += 1;

                    if (!window.Optanon) {
                        // clear the interval, if max retries reached
                        if (
                            optanonExistsIntervalCount > optanonExistsMaxRetries
                        ) {
                            window.clearInterval(optanonExistsInterval);
                        }

                        return;
                    }

                    if (!window.Optanon.GetDomainData().ShowAlertNotice) {
                        showOldBannerElements();
                    }

                    handleLiverampSetup();
                    // clear the interval, because we found Optanon
                    window.clearInterval(optanonExistsInterval);
                }, OPTANON_CHECK_INTERVAL);
            });

            setLanguage(window.location.pathname);

            if (isEnabledAnonymous) {
                appendScriptBySource(getDeleteAndGetCookieSource());
                appendOneTrustScriptBySource(appID, scriptURL);
            } else {
                appendOnetrustAndScripts(
                    {
                        environment,
                        startupCountry,
                        chapterLanguage,
                        chapterName,
                    },
                    appID,
                    scriptURL,
                );
            }
        });

        return;
    };

    return {
        run,
        findAndDeleteCookieScripts,
        getOneTrustConsentGroups: () => getOneTrustConsentGroups(window),
        getUserConsent: (): Promise<boot.user.UserConsent> =>
            Promise.resolve().then(() => {
                const userConsent = getOneTrustConsentGroups(window) || {};

                return {
                    performance: userConsent.C0002 === '1',
                    functional: userConsent.C0003 === '1',
                    targeting: userConsent.C0004 === '1',
                    googleAdsStorage: userConsent.C0007 === '1',
                    personalisedAds: userConsent.C0008 === '1',
                    gdprConsent: getGDPRConsent(window),
                    rampId: liverampInstance.getRampId(window),
                    pairId: liverampInstance.getPairId(window),
                    atsd: liverampInstance.getATSD(window),
                };
            }),
    };
}
